import React, { useState, useEffect } from "react";
import { Calendar as CalendarIcon, MessageSquare, Check, ArrowRight, User, Sparkles, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { WhatsAppChat } from "./WhatsAppChat";

export const BookingEngine = () => {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(4);
  const [includePool, setIncludePool] = useState(false);
  const [includeCampfire, setIncludeCampfire] = useState(false);
  
  // Simulation states
  const [isSimulating, setIsSimulating] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [extraBlocks, setExtraBlocks] = useState([]); // Selected dates that become blocked after simulation

  // Pre-defined blocked dates (June 2026 - June 1 is a Monday)
  const blockedDates = {
    6: "instagram",
    7: "instagram",
    8: "instagram",
    12: "airbnb",
    13: "airbnb",
    14: "airbnb",
    16: "mmt",
    17: "mmt",
    18: "whatsapp",
    19: "whatsapp",
    20: "whatsapp",
    22: "booking",
    23: "booking",
    24: "booking",
    25: "direct",
    26: "agoda",
    27: "agoda",
  };

  const isBlocked = (day) => {
    if (blockedDates[day]) return blockedDates[day];
    if (extraBlocks.includes(day)) return "whatsapp";
    return null;
  };

  const handleDateClick = (day) => {
    if (isBlocked(day)) return;
    if (bookingConfirmed) {
      // Reset if already confirmed
      resetAll();
    }

    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(day);
      setCheckOut(null);
    } else if (day > checkIn) {
      // Check if there are any blocked dates in between
      let hasBlockedBetween = false;
      for (let d = checkIn + 1; d < day; d++) {
        if (isBlocked(d)) hasBlockedBetween = true;
      }
      if (hasBlockedBetween) {
        // Reset and set as check-in
        setCheckIn(day);
      } else {
        setCheckOut(day);
      }
    } else {
      setCheckIn(day);
    }
  };

  const getDayClass = (day) => {
    const blockedType = isBlocked(day);
    if (blockedType) {
      if (blockedType === "airbnb") return "bg-rose-50 text-rose-500 border border-rose-100 cursor-not-allowed font-medium opacity-80";
      if (blockedType === "booking") return "bg-blue-50 text-blue-600 border border-blue-100 cursor-not-allowed font-medium opacity-80";
      if (blockedType === "mmt") return "bg-red-50 text-red-500 border border-red-100 cursor-not-allowed font-medium opacity-80";
      if (blockedType === "agoda") return "bg-purple-50 text-purple-500 border border-purple-100 cursor-not-allowed font-medium opacity-80";
      if (blockedType === "instagram") return "bg-indigo-50 text-indigo-500 border border-indigo-100 cursor-not-allowed font-medium opacity-80";
      if (blockedType === "whatsapp") return "bg-cyan-50 text-cyan-600 border border-cyan-100/70 cursor-not-allowed font-medium opacity-80";
      if (blockedType === "direct") return "bg-slate-50 text-slate-500 border border-slate-200 cursor-not-allowed font-medium opacity-80";
      return "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed opacity-50";
    }

    if (day === checkIn || day === checkOut) {
      return "bg-cyan-600 text-white font-bold scale-105 shadow-md shadow-cyan-600/20";
    }

    if (checkIn && checkOut && day > checkIn && day < checkOut) {
      return "bg-cyan-50 text-cyan-600 border border-cyan-100/50 font-semibold";
    }

    return "bg-white hover:bg-slate-50 border border-slate-100 text-slate-700 cursor-pointer font-medium hover:scale-105 transition-transform";
  };

  const getDayLabel = (day) => {
    const blockedType = isBlocked(day);
    if (blockedType === "airbnb") return (
      <>
        <span className="hidden sm:inline">Airbnb</span>
        <span className="sm:hidden">AB</span>
      </>
    );
    if (blockedType === "booking") return (
      <>
        <span className="hidden sm:inline">Booking</span>
        <span className="sm:hidden">BC</span>
      </>
    );
    if (blockedType === "mmt") return (
      <>
        <span className="hidden sm:inline">MakeMyTrip</span>
        <span className="sm:hidden">MMT</span>
      </>
    );
    if (blockedType === "agoda") return (
      <>
        <span className="hidden sm:inline">Agoda</span>
        <span className="sm:hidden">Agoda</span>
      </>
    );
    if (blockedType === "instagram") return (
      <>
        <span className="hidden sm:inline">Sagent (IG)</span>
        <span className="sm:hidden">IG</span>
      </>
    );
    if (blockedType === "whatsapp") return (
      <>
        <span className="hidden sm:inline">Sagent (WA)</span>
        <span className="sm:hidden">WA</span>
      </>
    );
    if (blockedType === "direct") return (
      <>
        <span className="hidden sm:inline">Direct</span>
        <span className="sm:hidden">DR</span>
      </>
    );
    return "";
  };

  const resetAll = () => {
    setCheckIn(null);
    setCheckOut(null);
    setChatMessages([]);
    setIsSimulating(false);
    setBookingConfirmed(false);
  };

  const nights = checkIn && checkOut ? checkOut - checkIn : 0;
  const basePricePerNight = 18000;
  const baseRent = nights * basePricePerNight;
  const poolCost = includePool ? nights * 2000 : 0;
  const campfireCost = includeCampfire ? 1500 : 0;
  const subtotal = baseRent + poolCost + campfireCost;
  const discount = nights >= 2 ? Math.round(baseRent * 0.1) : 0; // 10% discount for 2+ nights
  const total = subtotal - discount;

  // Simulate Sagent WhatsApp conversation
  const startSagentSimulation = () => {
    if (!checkIn || !checkOut) return;
    setIsSimulating(true);
    setChatMessages([]);
    setBookingConfirmed(false);

    const checkInDate = `June ${checkIn}, 2026`;
    const checkOutDate = `June ${checkOut}, 2026`;

    const dialog = [
      {
        delay: 500,
        message: {
          text: `Hi, is Forest Villa available from ${checkInDate} to ${checkOutDate} for ${guests} people?`,
          isUser: true,
          time: "12:00"
        }
      },
      {
        delay: 1800,
        message: {
          text: `Hi! Yes, Forest Villa is available from ${checkInDate} to ${checkOutDate} (${nights} night${nights > 1 ? 's' : ''}).\n\nStandard rate is ₹${baseRent.toLocaleString()} for ${guests} guests.\n\nWould you like me to reserve the dates?`,
          isUser: false,
          time: "12:00"
        }
      },
      {
        delay: 3200,
        message: {
          text: `Can you do a discount? We'd also like ${includePool ? 'pool access' : 'pool access and campfire'}.`,
          isUser: true,
          time: "12:02"
        }
      },
      {
        delay: 5000,
        message: {
          text: `Let me calculate the best offer for you. \n\nFor ${nights} night${nights > 1 ? 's' : ''}, I can discount the base stay to ₹${(baseRent - discount).toLocaleString()}.\n\nIncluding pool access and campfire, the final total is ₹${total.toLocaleString()}.\n\nShall I lock this in and send the secure booking link?`,
          isUser: false,
          time: "12:02"
        }
      },
      {
        delay: 6800,
        message: {
          text: `Yes, perfect! Please send the link.`,
          isUser: true,
          time: "12:03"
        }
      },
      {
        delay: 8200,
        message: {
          text: `Awesome! I've reserved the villa for you. \n\nHere is your payment link: https://sagent.ai/pay/forest-villa-${checkIn}-${checkOut}\n\nOnce paid, your confirmation voucher will be sent here automatically. See you soon! 🌴`,
          isUser: false,
          time: "12:03"
        }
      }
    ];

    // Play messages sequentially
    dialog.forEach((step, index) => {
      setTimeout(() => {
        setChatMessages(prev => [...prev, step.message]);
        
        // At the last message, confirm the booking and block calendar dates
        if (index === dialog.length - 1) {
          setIsSimulating(false);
          setBookingConfirmed(true);
          
          // Add dates to blocked list
          const bookedDays = [];
          for (let d = checkIn; d < checkOut; d++) {
            bookedDays.push(d);
          }
          setExtraBlocks(prev => [...prev, ...bookedDays]);
        }
      }, step.delay);
    });
  };



  // Generate 30 days grid of June 2026 (starts on Monday)
  const days = Array.from({ length: 30 }, (_, i) => i + 1);

  return (
    <section id="booking-engine" className="py-24 md:py-32 bg-white relative overflow-hidden" data-testid="booking-engine-section">
      <div className="absolute inset-0 bg-grid-small opacity-10" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="mb-16 text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-600/10 border border-cyan-600/20 text-cyan-700 text-xs font-bold tracking-widest uppercase mb-2">
            <Sparkles className="w-3.5 h-3.5" />
            Interactive Preview
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 font-['Lexend'] tracking-tight">
            Free <span className="text-cyan-600">Booking Engine</span> & Reservation Calendar
          </h2>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Let customers book directly from your website. Sagent syncs the calendar instantly and closes sales on WhatsApp automatically.
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          
          {/* Left: Interactive Calendar & Pricing (7 columns) */}
          <div className="lg:col-span-7 space-y-6">
            <Card className="border-black/5 shadow-xl shadow-slate-100 rounded-[2rem] overflow-hidden">
              <div className="bg-slate-900 px-6 py-5 flex items-center justify-between text-white">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-5 h-5 text-cyan-400" />
                  <span className="font-bold font-['Lexend'] text-sm tracking-wide">June 2026</span>
                </div>
                {checkIn && (
                  <button onClick={resetAll} className="text-xs text-slate-300 hover:text-white flex items-center gap-1.5 transition-colors">
                    <RefreshCw className="w-3 h-3" /> Clear Selection
                  </button>
                )}
              </div>
              <CardContent className="p-6 sm:p-8 bg-white">
                
                {/* Calendar Days of Week headers */}
                <div className="grid grid-cols-7 gap-2 text-center text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">
                  <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
                </div>

                {/* Calendar Days Grid */}
                <div className="grid grid-cols-7 gap-2">
                  {days.map((day) => (
                    <div
                      key={day}
                      onClick={() => handleDateClick(day)}
                      className={`relative aspect-square flex flex-col items-center justify-center rounded-xl transition-all select-none p-1 ${getDayClass(day)}`}
                    >
                      <span className="text-sm font-bold">{day}</span>
                      <span className="text-[7px] md:text-[8px] font-bold mt-1 uppercase tracking-tighter truncate w-full text-center">
                        {getDayLabel(day)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Calendar Legend */}
                <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap gap-x-6 gap-y-4 justify-center text-[10px] sm:text-xs font-bold text-slate-500">
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded bg-cyan-50 border border-cyan-100/70 flex-shrink-0" />
                    <span>Sagent (WhatsApp)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded bg-indigo-50 border border-indigo-100 flex-shrink-0" />
                    <span>Sagent (Instagram)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded bg-rose-50 border border-rose-100 flex-shrink-0" />
                    <span>Airbnb</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded bg-blue-50 border border-blue-100 flex-shrink-0" />
                    <span>Booking.com</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded bg-red-50 border border-red-100 flex-shrink-0" />
                    <span>MakeMyTrip</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded bg-purple-50 border border-purple-100 flex-shrink-0" />
                    <span>Agoda</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded bg-slate-50 border border-slate-200 flex-shrink-0" />
                    <span>Direct Booking</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded bg-white border border-slate-100 flex-shrink-0" />
                    <span>Available</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Selection/Pricing Panel */}
            {checkIn && checkOut && (
              <Card className="border-black/5 shadow-xl shadow-slate-100 rounded-[2rem] overflow-hidden animate-fade-in-up">
                <CardContent className="p-6 sm:p-8 bg-slate-50/50">
                  <h3 className="text-lg font-bold text-slate-900 mb-6 font-['Lexend']">Reservation Invoice Details</h3>
                  <div className="grid sm:grid-cols-2 gap-6 items-start">
                    
                    {/* Add-ons Selection */}
                    <div className="space-y-4 bg-white p-5 rounded-2xl border border-black/5">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Configure Add-Ons</p>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includePool}
                          onChange={(e) => setIncludePool(e.target.checked)}
                          disabled={isSimulating || bookingConfirmed}
                          className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 h-4 w-4"
                        />
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Private Pool Access</p>
                          <p className="text-[10px] text-slate-500">₹2,000 per night</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeCampfire}
                          onChange={(e) => setIncludeCampfire(e.target.checked)}
                          disabled={isSimulating || bookingConfirmed}
                          className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 h-4 w-4"
                        />
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Campfire Setup</p>
                          <p className="text-[10px] text-slate-500">₹1,500 flat charge</p>
                        </div>
                      </label>
                    </div>

                    {/* Pricing summary */}
                    <div className="space-y-3 bg-white p-5 rounded-2xl border border-black/5 font-medium">
                      <div className="flex justify-between text-sm text-slate-500">
                        <span>Base Rent ({nights} night{nights > 1 ? 's' : ''})</span>
                        <span>₹{baseRent.toLocaleString()}</span>
                      </div>
                      {includePool && (
                        <div className="flex justify-between text-sm text-slate-500">
                          <span>Pool Access</span>
                          <span>₹{poolCost.toLocaleString()}</span>
                        </div>
                      )}
                      {includeCampfire && (
                        <div className="flex justify-between text-sm text-slate-500">
                          <span>Campfire Setup</span>
                          <span>₹{campfireCost.toLocaleString()}</span>
                        </div>
                      )}
                      {discount > 0 && (
                        <div className="flex justify-between text-sm text-emerald-600 font-bold">
                          <span>10% Sagent Discount</span>
                          <span>-₹{discount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="h-px bg-slate-100 my-2" />
                      <div className="flex justify-between text-base text-slate-900 font-extrabold font-['Lexend']">
                        <span>Total Price</span>
                        <span className="text-cyan-600">₹{total.toLocaleString()}</span>
                      </div>
                    </div>

                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: WhatsApp Booking Simulation (5 columns) */}
          <div className="lg:col-span-5 h-full">
            <Card className="border-black/5 shadow-2xl shadow-cyan-900/5 rounded-[2.5rem] overflow-hidden flex flex-col bg-slate-50">
              <div className="p-6 bg-slate-900 text-white flex items-center justify-between border-b border-white/5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm leading-none font-['Lexend']">Sagent Booking Engine</h3>
                    <p className="text-[9px] text-cyan-400 font-bold tracking-wider uppercase mt-1">Simulated Direct Integration</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 flex-1 flex flex-col justify-center min-h-[350px]">
                {(!checkIn || !checkOut) ? (
                  <div className="text-center space-y-4 py-12 px-4">
                    <div className="w-16 h-16 rounded-3xl bg-cyan-600/10 flex items-center justify-center mx-auto text-cyan-600">
                      <CalendarIcon className="w-8 h-8 animate-pulse" />
                    </div>
                    <h4 className="text-base font-bold text-slate-800 font-['Lexend']">Select Dates to Start</h4>
                    <p className="text-xs text-slate-500 leading-relaxed max-w-xs mx-auto">
                      Pick any available check-in and check-out dates on the calendar to test direct Sagent integration.
                    </p>
                  </div>
                ) : chatMessages.length === 0 ? (
                  <div className="text-center space-y-5 py-12 px-4">
                    <div className="w-16 h-16 rounded-3xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mx-auto text-emerald-600">
                      <Check className="w-8 h-8" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-base font-bold text-slate-800 font-['Lexend']">Dates Selected Successfully!</h4>
                      <p className="text-xs text-slate-500 font-semibold uppercase">June {checkIn} → June {checkOut} ({nights} night{nights > 1 ? 's' : ''})</p>
                    </div>
                    <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
                      Click the button below to see how Sagent dynamically initiates a WhatsApp chat, negotiates discounts, and blocks your calendar.
                    </p>
                    <Button
                      onClick={startSagentSimulation}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full px-6 py-5 font-bold w-full max-w-xs mx-auto shadow-md shadow-cyan-600/15 btn-lift"
                    >
                      Inquire & Book via Sagent
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 flex flex-col">
                    <WhatsAppChat
                      showHeader={false}
                      messages={chatMessages}
                      className="shadow-sm border border-slate-100 rounded-2xl overflow-hidden scale-95 md:scale-100 origin-top bg-white"
                    />

                    {isSimulating && (
                      <div className="flex justify-center items-center gap-1.5 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 animate-bounce" style={{ animationDelay: '300ms' }} />
                        <span>Sagent is responding</span>
                      </div>
                    )}

                    {bookingConfirmed && (
                      <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 text-center space-y-2 animate-fade-in-up mt-2">
                        <p className="text-xs text-emerald-800 font-extrabold flex items-center justify-center gap-2">
                          <Check className="w-4 h-4" /> Calendar Blocked & Paid
                        </p>
                        <p className="text-[10px] text-emerald-600 leading-relaxed font-semibold">
                          The calendar dates <strong>June {checkIn} to June {checkOut}</strong> have been marked blocked on your direct booking engine automatically!
                        </p>
                        <Button 
                          onClick={resetAll}
                          variant="outline" 
                          className="border-emerald-200 text-emerald-700 hover:bg-emerald-100/50 rounded-full text-xs font-bold px-4 h-8 mt-2 mx-auto"
                        >
                          Book Another Stay
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

        </div>

      </div>
    </section>
  );
};

export default BookingEngine;
